import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import FadingContainer from '../FadingContainer/FadingContainer';
import { ReactComponent as CheckMarkIcon } from '../../SVGAssets/check-mark.svg';
import { ReactComponent as ErrorCircleIcon } from '../../SVGAssets/error-circle.svg';
import { ReactComponent as WarningCircleIcon } from '../../SVGAssets/warning-circle.svg';

import './SystemMessage.scss';

const STATUS_ICONS = {
  success: CheckMarkIcon,
  error: ErrorCircleIcon,
  warning: null,
  info: null,
};

/**
 * One way to tell the user how something the app just did turned out.
 *
 * Before this there were four: a centred italic div in EndPersonalization, a
 * yellow warning box in PersonalizeWelcomeMessage, a bespoke failure list in
 * CrmPushStatusMessage, and DetailDialog over on the residence side. Same job,
 * four looks, four sets of markup to keep in step.
 *
 * This is presentational only — callers own the state that decides whether
 * there is anything to say. Field-level validation stays with FormFieldError;
 * that one sits inside a form control, this one reports on an operation.
 *
 * Renders nothing when there is nothing to report, so callers can hand it a
 * possibly-empty message without guarding the call site.
 */
const SystemMessage = ({
  status,
  title,
  message,
  items,
  fading,
  showIcon,
  testId,
  className,
  children,
}) => {
  const hasContent = Boolean(title || message || items.length || children);

  if (!hasContent) {
    return null;
  }

  const Icon = showIcon ? STATUS_ICONS[status] : null;

  const body = (
    <div
      className={classNames('systemMessage', `systemMessage--${status}`, className)}
      data-testid={testId || `system-message:${status}`}
    >
      {Icon && (
        <div className="systemMessage__icon">
          <Icon />
        </div>
      )}

      <div className="systemMessage__body">
        {title && <h4 className="systemMessage__title">{title}</h4>}
        {message && <p className="systemMessage__text">{message}</p>}

        {items.length > 0 && (
          <ul className="systemMessage__list">
            {items.map((item) => (
              <li
                key={item.key}
                className="systemMessage__listItem"
                data-testid={item.testId}
              >
                {item.content}
              </li>
            ))}
          </ul>
        )}

        {children}
      </div>
    </div>
  );

  return fading ? <FadingContainer>{body}</FadingContainer> : body;
};

SystemMessage.propTypes = {
  status: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.node,
  message: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired,
    testId: PropTypes.string,
  })),
  fading: PropTypes.bool,
  showIcon: PropTypes.bool,
  testId: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

SystemMessage.defaultProps = {
  status: 'info',
  title: null,
  message: null,
  items: [],
  fading: false,
  showIcon: true,
  testId: null,
  className: null,
  children: null,
};

export default SystemMessage;
